import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FilterOption, ListQueryParams, ListSearch, OptionItem, SortOption, UserDetail, UserInfo } from '@ca/core/models';
import { UserService } from '@ca/core/services';
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import deepEqual from 'deep-equal';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, map, switchMap, tap } from 'rxjs/operators';
import { FullNameWithEmailPipe } from '../../pipes/full-name-with-email/full-name-with-email.pipe';
import { FullNamePipe } from '../../pipes/full-name/full-name.pipe';

@Component({
    selector: 'ca-list-shell',
    templateUrl: './list-shell.component.html',
    styleUrls: ['./list-shell.component.scss'],
})
export class ListShellComponent implements OnInit, OnChanges {
    @Input() emptyListMessage: string;
    @Input() pageLink: string;
    @Input() clearSearchQuery: number; // set to a new number (for example timestamp) to clear search field without triggering the search
    @Input() fileTypeFilterOptions: string[];
    /**
     * Pass a ListSearch object here to predefine the search and sort state.
     * If undefined, defaultSearchOptions is taken.
     * You can also change this object from outside, the component will react to the changes and retrigger search.
     *
     * ATTENTION: Setting this only works with updateParameterQuery = false. It's not implemented yet to predefine search and sort options
     * when they're taken from the URL.
     */
    @Input() searchAndSortOptions: ListSearch;
    @Input() isCZIConversionInProgress: boolean = false;
    @Input() isPageSizeModifiable: boolean = false;
    @Input() hideFilterAndSearchBar = false;
    @Input() hidePagination = false;
    @Input() isSearchInputShown = true;
    @Input() showFilterCheckBoxes: boolean = false;
    @Input() isLoading = false;
    @Input() isSearchLoading = false;
    @Input() totalItemsCount: number = 0;
    @Input() showUsersFilter = false;
    @Input() isResetButtonShown: boolean = false;
    @Input() showCategoryButton: boolean = false;
    @Input() pageSize: number = 10;
    @Input() stateOptions: OptionItem<string>[] = [];
    @Input() machineLearningWorkflowTypeOptions: OptionItem<string>[] = [];
    @Input() categoryOptions: OptionItem<string>[] = [];
    @Input() userTypeOptions: OptionItem<string>[] = [];
    @Input() complianceOptions: OptionItem<string>[] = [];
    @Input() isUploadModal: boolean = false;
    @Input() pageSizeStorageKey?: string = '10';
    @Input() isUploadInProgress?: boolean;
    @Input() useFirstSortOptionAsDefault?: boolean = true;
    @Input() isConversionFailedVisible?: boolean;
    @Input() isInvalidFilesVisibile?: boolean;
    @Input() isAddedFilesVisible?: boolean;
    @Input() availableFilterOptions?: FilterOption[];
    @Input() sortOptions?: OptionItem<SortOption>[];
    @Input() uitPrefix?: string;

    /**
     * When we use this component inside of modal
     * we don't want to change our url (query parameter)
     *
     * PLEASE NOTE: When you have more than one instance of ListShellComponent on one page, you MUST (I)
     * set this parameter to false (Else the values will be updated in ALL components)
     */
    @Input() updateParameterQuery = true;
    /**
     * Skips the first search which is executed on initialize
     */
    @Input() skipTriggerSearchOnInit = false;

    @Output() onTriggerSearchAndSort: EventEmitter<ListSearch> = new EventEmitter();
    @Output() resetButtonClicked = new EventEmitter<void>();
    @Output() pageSizeChange = new EventEmitter<number>();

    @ViewChild('searchUsersInput') searchUsersInput: ElementRef;

    filterOptions: OptionItem<FilterOption>[] = [];
    sortByField: OptionItem<SortOption>;
    selectedMachineLearningWorkflowTypeOptions: OptionItem<string>[];
    selectedStateOption: OptionItem<string> | undefined;
    selectedCategoryOptions: OptionItem<string>[];
    selectedUserTypeOptions: OptionItem<string>[];
    selectedFilterOptions: OptionItem<FilterOption>[];
    selectedFileTypeFilterOption: string[] = [];
    userToAdd?: UserDetail;
    users: UserDetail[] = [];
    filterTranslations: { [key: string]: string } = {
        compliance: 'Compliant',
        isFeatured: 'Featured',
        isCommunityContent: 'From community',
        isOpenSource: 'Code available',
    };
    isInit = true;
    usersLoading = false;
    isUploadModalDisplayed = this.isUploadModal;

    constructor(
        public route: ActivatedRoute,
        private fullNamePipe: FullNamePipe,
        private fullNameWithEmailPipe: FullNameWithEmailPipe,
        private userService: UserService,
        private router: Router,
    ) {
        this.searchUsers$ = this.searchUsers$.bind(this);
    }

    ngOnInit(): void {
        if (this.searchAndSortOptions == null) {
            this.searchAndSortOptions = this.initializeDefaultSearchAndSortOptions();
        }

        this.applySearchAndSortOptionsInUiControls(this.searchAndSortOptions);
        if (this.availableFilterOptions) {
            this.availableFilterOptions.forEach(filterItem => {
                this.filterOptions.push(new OptionItem<FilterOption>(filterItem, this.filterTranslations[filterItem]));
            });
        }

        if (this.updateParameterQuery) {
            this.route.queryParamMap
                .pipe(
                    tap(queryParamMap => {
                        const searchQuery = queryParamMap.get(ListQueryParams.Search);
                        const pageQuery = queryParamMap.get(ListQueryParams.Page);
                        const categoryQuery = queryParamMap.get(ListQueryParams.Category);
                        const mlWorkflowTypeQuery = queryParamMap.get(ListQueryParams.WorkflowTypes);
                        const sortByQuery = <SortOption>queryParamMap.get(ListQueryParams.SortBy);
                        const complianceQuery = queryParamMap.get(ListQueryParams.Compliance);
                        const isOpenSourceQuery = queryParamMap.get(ListQueryParams.IsOpenSource);
                        const isCommunityContent = queryParamMap.get(ListQueryParams.IsCommunityContent);
                        const isFeatured = queryParamMap.get(ListQueryParams.IsFeatured);
                        const users = queryParamMap.get(ListQueryParams.Users);
                        const state = queryParamMap.get(ListQueryParams.State);
                        const folderId = queryParamMap.get(ListQueryParams.FolderId as string);
                        this.searchAndSortOptions = this.applyQueryParamsToListSearch(
                            searchQuery,
                            pageQuery,
                            categoryQuery,
                            mlWorkflowTypeQuery,
                            sortByQuery,
                            complianceQuery,
                            isOpenSourceQuery,
                            isCommunityContent,
                            isFeatured,
                            users,
                            state,
                            folderId,
                        );

                        this.applySearchAndSortOptionsInUiControls(this.searchAndSortOptions);

                        if (this.skipTriggerSearchOnInit && this.isInit) {
                            this.isInit = false;
                            return;
                        }

                        this.triggerSearch();
                    }),
                )
                .subscribe();
        } else {
            if (!this.skipTriggerSearchOnInit) {
                this.triggerSearch();
            }
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.categoryOptions?.currentValue && this.searchAndSortOptions != null) {
            this.categoryOptions = changes.categoryOptions.currentValue;
            const categoryIds = this.searchAndSortOptions.categoryIds!.split(',');
            this.selectedCategoryOptions = this.categoryOptions.filter(category => categoryIds.includes(category.value));
        }
        if (changes?.machineLearningWorkflowTypeOptions?.currentValue && this.searchAndSortOptions != null) {
            this.machineLearningWorkflowTypeOptions = changes.machineLearningWorkflowTypeOptions.currentValue;
            const mlWorkflowTypes = this.searchAndSortOptions.workflowTypeIds!.split(',');
            this.selectedMachineLearningWorkflowTypeOptions = this.machineLearningWorkflowTypeOptions.filter(
                mlWorkflowType => mlWorkflowTypes.indexOf(mlWorkflowType.value) > -1,
            );
        }
        if (changes?.userTypeOptions?.currentValue && this.searchAndSortOptions != null) {
            this.userTypeOptions = changes.userTypeOptions.currentValue;
            const userTypes = this.searchAndSortOptions.userTypes!.split(',');
            this.selectedUserTypeOptions = this.userTypeOptions.filter(userType => userTypes.includes(userType.value));
        }
        if (
            changes.searchAndSortOptions &&
            !changes.searchAndSortOptions.isFirstChange() &&
            changes.searchAndSortOptions.currentValue != null &&
            !deepEqual(changes.searchAndSortOptions.currentValue, changes.searchAndSortOptions.previousValue)
        ) {
            this.searchAndSortOptions = changes.searchAndSortOptions.currentValue;
            this.applySearchAndSortOptionsInUiControls(changes.searchAndSortOptions.currentValue as ListSearch);
            this.triggerSearch();
        }
        if (
            changes.clearSearchQuery &&
            !changes.clearSearchQuery.isFirstChange() &&
            changes.clearSearchQuery.currentValue != null &&
            changes.clearSearchQuery.currentValue !== changes.clearSearchQuery.previousValue
        ) {
            this.searchAndSortOptions.searchQuery = '';
            this.searchAndSortOptions.pageNumber = 1;
        }
    }

    onChildPageSizeChange(newSize: number): void {
        this.pageSize = newSize;
        this.pageSizeChange.emit(newSize);
    }

    onSearchTermChange(searchTerm: string): void {
        this.searchAndSortOptions.searchQuery = searchTerm;

        this.resetPagination();
        this.updateQueryParameter({
            [ListQueryParams.Page]: this.searchAndSortOptions.pageNumber.toString(),
            [ListQueryParams.Search]: this.searchAndSortOptions.searchQuery,
        });
    }

    onSortOptionsChange(event: OptionItem<SortOption>): void {
        this.resetPagination();
        this.updateQueryParameter({
            [ListQueryParams.Search]: this.getSearchQueryParameter(),
            [ListQueryParams.Page]: (this.searchAndSortOptions.pageNumber = 1).toString(),
            [ListQueryParams.SortBy]: event.value || null,
        });
    }

    onMachineLearningWorkflowTypeOptionsChange(event: OptionItem<string>[]): void {
        this.searchAndSortOptions.workflowTypeIds = event.map(e => e.value).join(',');
        this.selectedMachineLearningWorkflowTypeOptions = this.machineLearningWorkflowTypeOptions.filter(
            mlWorkflowTypeOption => this.searchAndSortOptions.workflowTypeIds!.split(',').indexOf(mlWorkflowTypeOption.value) > -1,
        );

        this.resetPagination();
        this.updateQueryParameter({
            [ListQueryParams.Page]: (this.searchAndSortOptions.pageNumber = 1).toString(),
            [ListQueryParams.WorkflowTypes]: this.searchAndSortOptions.workflowTypeIds || null,
        });
    }

    onUserTypeOptionsChange(event: OptionItem<string>[]): void {
        this.searchAndSortOptions.userTypes = event.map(e => e.value).join(',');
        this.selectedUserTypeOptions = this.userTypeOptions.filter(userTypeOption =>
            this.searchAndSortOptions.userTypes!.split(',').includes(userTypeOption.value),
        );

        this.resetPagination();
        this.updateQueryParameter({
            [ListQueryParams.Page]: (this.searchAndSortOptions.pageNumber = 1).toString(),
            [ListQueryParams.UserType]: this.searchAndSortOptions.userTypes || null,
        });
    }

    onStateOptionsChange(event: OptionItem<string>): void {
        this.searchAndSortOptions.state = event?.value;
        this.selectedStateOption = this.stateOptions.find(state => this.searchAndSortOptions.state === state.value);

        this.resetPagination();
        this.updateQueryParameter({
            [ListQueryParams.Page]: this.searchAndSortOptions.pageNumber.toString(),
            [ListQueryParams.State]: this.searchAndSortOptions.state,
        });
    }

    onCategoryOptionsChange(event: OptionItem<string>[]): void {
        this.searchAndSortOptions.categoryIds = event.map(e => e.value).join(',');
        this.selectedCategoryOptions = this.categoryOptions.filter(category => this.searchAndSortOptions.categoryIds!.includes(category.value));

        this.resetPagination();
        this.updateQueryParameter({
            [ListQueryParams.Page]: this.searchAndSortOptions.pageNumber?.toString(),
            [ListQueryParams.Category]: this.searchAndSortOptions.categoryIds,
        });
    }

    onFilterOptionsChange(event: OptionItem<FilterOption>[]): void {
        const filterValues = event.map(e => e.value).join(',');

        this.selectedFilterOptions = this.filterOptions.filter(filter => filterValues.includes(filter.value));

        const isCompliantItem = this.selectedFilterOptions.find(option => option.value === 'compliance');
        let isCompliant = false;
        if (isCompliantItem) {
            isCompliant = true;
        }
        this.searchAndSortOptions.complianceIds = isCompliant ? this.complianceOptions[0].value : '';

        const isFeaturedItem = this.selectedFilterOptions.find(option => option.value === 'isFeatured');
        if (isFeaturedItem) {
            this.searchAndSortOptions.isFeatured = true;
        } else {
            this.searchAndSortOptions.isFeatured = false;
        }

        const isCommunityContentItem = this.selectedFilterOptions.find(option => option.value === 'isCommunityContent');
        if (isCommunityContentItem) {
            this.searchAndSortOptions.isCommunityContent = true;
        } else {
            this.searchAndSortOptions.isCommunityContent = false;
        }

        const isOpenSourceItem = this.selectedFilterOptions.find(option => option.value === 'isOpenSource');
        if (isOpenSourceItem) {
            this.searchAndSortOptions.isOpenSource = true;
        } else {
            this.searchAndSortOptions.isOpenSource = false;
        }
        this.resetPagination();
        this.updateQueryParameter({
            [ListQueryParams.Page]: this.searchAndSortOptions.pageNumber.toString(),
            [ListQueryParams.Compliance]: this.searchAndSortOptions.complianceIds || null,
            [ListQueryParams.IsFeatured]: this.searchAndSortOptions.isFeatured || null,
            [ListQueryParams.IsCommunityContent]: this.searchAndSortOptions.isCommunityContent || null,
            [ListQueryParams.IsOpenSource]: this.searchAndSortOptions.isOpenSource || null,
        });
    }

    onFileTypeOptionsChange(valuesAr: string[]): void {
        const values = valuesAr.join(',');
        this.resetPagination();
        this.searchAndSortOptions.fileTypes = values;
        this.updateQueryParameter({
            [ListQueryParams.Page]: this.searchAndSortOptions.pageNumber.toString(),
            [ListQueryParams.FileType]: this.searchAndSortOptions.fileTypes || null,
        });
    }

    onPageChange(event: number): void {
        this.searchAndSortOptions.pageNumber = event;
        this.updateQueryParameter({
            [ListQueryParams.Search]: this.getSearchQueryParameter(),
            [ListQueryParams.Page]: this.searchAndSortOptions.pageNumber.toString(),
        });
    }

    onFolderChange(event: string): void {
        this.searchAndSortOptions.folderId = event;
        this.searchAndSortOptions.searchQuery = '';
        this.searchAndSortOptions.pageNumber = 1;
        this.updateQueryParameter({
            [ListQueryParams.Page]: this.searchAndSortOptions.pageNumber.toString(),
            [ListQueryParams.FolderId]: this.searchAndSortOptions.folderId.toString(),
            [ListQueryParams.Search]: this.searchAndSortOptions.searchQuery.toString(),
        });
    }

    resetParams(): void {
        this.initializeDefaultSearchAndSortOptions();
    }

    triggerSearch(): void {
        this.onTriggerSearchAndSort.emit({
            pageNumber: this.searchAndSortOptions.pageNumber,
            pageSize: this.isPageSizeModifiable ? this.pageSize : this.searchAndSortOptions.pageSize,
            sortBy: this.sortByField?.value,
            fileTypes: this.searchAndSortOptions?.fileTypes,
            searchQuery: this.searchAndSortOptions.searchQuery,
            workflowTypeIds: this.searchAndSortOptions.workflowTypeIds,
            userTypes: this.searchAndSortOptions.userTypes,
            categoryIds: this.searchAndSortOptions.categoryIds,
            complianceIds: this.searchAndSortOptions.complianceIds,
            isOpenSource: this.searchAndSortOptions.isOpenSource,
            isFeatured: this.searchAndSortOptions.isFeatured,
            isCommunityContent: this.searchAndSortOptions.isCommunityContent,
            users: this.searchAndSortOptions.users,
            state: this.searchAndSortOptions.state,
            folderId: this.searchAndSortOptions.folderId,
        });
    }

    // user filtering
    formatUser = (userDetail: UserDetail): string => this.fullNamePipe.transform(userDetail);

    formatUserWithEmail = (userDetail: UserDetail): string => this.fullNameWithEmailPipe.transform(userDetail);

    searchUsers$(text$: Observable<string>): Observable<UserDetail[]> {
        return text$.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            switchMap(textSearch => (textSearch.length < 2 ? [] : this.userService.searchUsersInfoByNameOrUserName$(textSearch))),
        );
    }

    selectUser(event: NgbTypeaheadSelectItemEvent | null, users?: UserDetail[]): void {
        if (event) {
            const userToAdd = event.item as UserDetail;
            if (userToAdd != null && !this.users.some((userDetail: UserDetail) => userDetail.id === userToAdd!.id)) {
                this.users.push(userToAdd);
                this.onPersonFilterChange();
            }
            this.userToAdd = undefined;
            setTimeout(() => {
                // unfortunately the only way to remove the content of the typeahead
                this.searchUsersInput.nativeElement.value = '';
            });
        } else if (users) {
            this.users = users;
            this.onPersonFilterChange();
        }
    }

    removePerson(userDetail: UserDetail): void {
        const index: number = this.users.findIndex((u: UserDetail) => u.id === userDetail.id);

        if (index > -1) {
            this.users.splice(index, 1);
            this.onPersonFilterChange();
        }
    }

    private onPersonFilterChange(userIds?: string): void {
        this.searchAndSortOptions.users = userIds || this.users.map(user => user.id).join(',');
        this.resetPagination();
        this.updateQueryParameter({
            [ListQueryParams.Page]: this.searchAndSortOptions.pageNumber.toString(),
            [ListQueryParams.Users]: this.searchAndSortOptions.users,
        });
    }

    /**
     * Takes the searchAndSortOptions as parameter and applies all values (which are needed for rendering the UI)
     */
    private applySearchAndSortOptionsInUiControls(searchAndSortOptions: ListSearch): void {
        if (searchAndSortOptions.sortBy && this.sortOptions) {
            const sortOption = Object.values(this.sortOptions).find(option => option.value === this.searchAndSortOptions.sortBy);
            if (sortOption) {
                this.sortByField = sortOption;
            }
        }
        // fallback: if no sortBy is selected, select the first one by default
        if (this.useFirstSortOptionAsDefault && this.sortByField == null && this.sortOptions != null && this.sortOptions.length > 0) {
            this.sortByField = this.sortOptions[0]; // First sort option as default
        }

        if (!this.categoryOptions) {
            this.categoryOptions = [];
        }

        if (searchAndSortOptions.categoryIds != null) {
            const categoryIds = searchAndSortOptions.categoryIds.split(',');
            this.selectedCategoryOptions = this.categoryOptions.filter(category => categoryIds.includes(category.value));
        }

        if (searchAndSortOptions.workflowTypeIds != null) {
            const mlWorkflowTypes = searchAndSortOptions.workflowTypeIds.split(',');
            this.selectedMachineLearningWorkflowTypeOptions = this.machineLearningWorkflowTypeOptions.filter(
                mlWorkflowType => mlWorkflowTypes.indexOf(mlWorkflowType.value) > -1,
            );
        }

        this.selectedFilterOptions = [];

        if (!this.complianceOptions) {
            this.complianceOptions = [];
        }

        if (searchAndSortOptions.complianceIds !== '') {
            this.selectedFilterOptions = this.filterOptions.filter(filter => filter.value === 'compliance');
        }

        if (searchAndSortOptions.isFeatured) {
            this.searchAndSortOptions.isFeatured = true;
            this.selectedFilterOptions.push(this.filterOptions.find(filter => filter.value === 'isFeatured')!);
        }

        if (searchAndSortOptions.isCommunityContent) {
            this.searchAndSortOptions.isCommunityContent = true;
            this.selectedFilterOptions.push(this.filterOptions.find(filter => filter.value === 'isCommunityContent')!);
        }

        if (searchAndSortOptions.isOpenSource) {
            this.searchAndSortOptions.isOpenSource = true;
            this.selectedFilterOptions.push(this.filterOptions.find(filter => filter.value === 'isOpenSource')!);
        }

        if (searchAndSortOptions.users) {
            const userIds: string[] = searchAndSortOptions.users.split(',');
            if (userIds.length > 0) {
                this.getUsers$(userIds)
                    .pipe(tap(users => (this.users = users)))
                    .subscribe();
            } else {
                this.users = [];
            }
        } else {
            this.users = [];
        }

        if (searchAndSortOptions.state) {
            this.selectedStateOption = this.stateOptions.find(state => searchAndSortOptions.state === state.value);
        }

        this.searchAndSortOptions.folderId = searchAndSortOptions.folderId;
    }

    private getUsers$(userIds: string[]): Observable<UserInfo[]> {
        this.usersLoading = true;
        return this.userService.getUserInfos$(userIds).pipe(
            map((userInfos: UserInfo[]) => {
                return userInfos.map(user => {
                    return { id: user.id, firstName: user.firstName, lastName: user.lastName, picture: user.picture, email: user.email } as UserInfo;
                });
            }),
            finalize(() => {
                this.usersLoading = false;
            }),
        );
    }

    /**
     * Applies the query params (from URL) to the listSearch object
     */
    private applyQueryParamsToListSearch(
        searchQuery: string | null,
        pageQuery: string | null,
        categoryQuery: string | null,
        mlWorkflowTypeQuery: string | null,
        sortByQuery: SortOption | null,
        complianceQuery: string | null,
        isOpenSourceQuery: string | null,
        isCommunityContentQuery: string | null,
        isFeaturedQuery: string | null,
        users: string | null,
        state: string | null,
        folderId: string | null,
    ): ListSearch {
        const listSearch = this.searchAndSortOptions;

        if (searchQuery != null) {
            listSearch.searchQuery = searchQuery;
        }

        if (pageQuery != null) {
            listSearch.pageNumber = parseInt(pageQuery, 10);
        }

        if (sortByQuery != null) {
            listSearch.sortBy = sortByQuery;
        }

        listSearch.categoryIds = categoryQuery || '';
        listSearch.workflowTypeIds = mlWorkflowTypeQuery || '';
        listSearch.complianceIds = complianceQuery || '';

        if (isFeaturedQuery == null) {
            listSearch.isFeatured = undefined;
        } else {
            listSearch.isFeatured = isFeaturedQuery === 'true' ? true : false;
        }

        if (isCommunityContentQuery == null) {
            listSearch.isCommunityContent = undefined;
        } else {
            listSearch.isCommunityContent = isCommunityContentQuery === 'true' ? true : false;
        }

        if (isOpenSourceQuery == null) {
            listSearch.isOpenSource = undefined;
        } else {
            listSearch.isOpenSource = isOpenSourceQuery === 'true' ? true : false;
        }

        if (users != null) {
            listSearch.users = users;
        }

        if (state != null) {
            listSearch.state = state;
        }

        listSearch.folderId = folderId || '';

        return listSearch;
    }

    private initializeDefaultSearchAndSortOptions(): ListSearch {
        if (this.searchAndSortOptions != null) {
            this.searchAndSortOptions.isFeatured = false;
            this.searchAndSortOptions.isCommunityContent = false;
            this.searchAndSortOptions.isOpenSource = false;
        }

        const defaultListSearch: ListSearch = {
            pageNumber: 1,
            pageSize: this.pageSize,
            searchQuery: '',
            sortBy: this.sortOptions ? this.sortOptions[0].value : '',
            complianceIds: '',
            categoryIds: '',
            workflowTypeIds: '',
            folderId: '',
        };

        return defaultListSearch;
    }

    private resetPagination(): void {
        if (this.searchAndSortOptions != null) {
            this.searchAndSortOptions.pageNumber = 1;
        }
    }

    /**
     * Updates the query parameters. Please be aware that the search is then triggered implicitly,
     * because the component reinitializes (triggerSearch() is then called in ngOnInit()).
     *
     * Reinitialization of the component is essential, because when you navigate in the browser history and only the query parameters change
     * (i.e. from /app/files?page=1&q=my-file to /app/files?page=1&q=my-other-file) the component should handle this change and search for my-other-file.
     */
    private updateQueryParameter(queryParams: Params): void {
        if (this.updateParameterQuery) {
            this.router
                .navigate([], {
                    relativeTo: this.route,
                    queryParams,
                    queryParamsHandling: 'merge',
                })
                .catch(err => {
                    throw new Error(`Adding query param failed: ${JSON.stringify(err)}`);
                });
        } else {
            this.triggerSearch();
        }
    }

    private getSearchQueryParameter(): string {
        // if searchQuery was cleared by using clearSearchQuery input make state in url query params
        // consistent with the state in this.searchAndSortOptions.searchQuery
        return this.route.snapshot.queryParams.q !== this.searchAndSortOptions.searchQuery
            ? this.searchAndSortOptions.searchQuery
            : this.route.snapshot.queryParams.q;
    }
}
